import React, { useState, useEffect } from 'react'
import {
    Button,
    Card,
    Col,
    Form,
    Row,
    DatePicker,
    message,
    Spin,
    Slider,
    Typography,
    Modal,
    Input,
    Checkbox
} from 'antd'
import { connect } from 'react-redux'
import moment from 'moment'
import AssetSearch from '../../components/AssetSearch'
import {
    searchGlobalAssetData
} from '../../appRedux/actions/Common'
import axios from '../../util/Api'

const { Text } = Typography

const mapStateToProps = ({ auth, commonData }) => {
    return {
        authUser: auth.authUser,
        loading: commonData.loading,
        assetData: commonData.assetSearchData,
        assetSearchLoading: commonData.assetSearchLoading
    }
}

function mapDispatchToProps(dispatch) {
    return {
        searchGlobalAssetData: (searchText, isOnlyIOTAsset = false) => dispatch(searchGlobalAssetData(searchText, isOnlyIOTAsset))
    }
}

function DailyReport(props) {
    const [formData, setFormData] = useState({
        startDate: null,
        endDate: null,
        startTime: '00:00:00', // Changed to string format
        endTime: '23:59:59', // Changed to string format
        assets: []
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [saveModalVisible, setSaveModalVisible] = useState(false)
    const [pendingDownload, setPendingDownload] = useState(null)
    const [customFilename, setCustomFilename] = useState('')
    const [allAssetChecked, setAllAssetChecked] = useState(false)

    const { getFieldDecorator } = props.form

    // Deep-link support: prefill Start/End Date and "All asset" from ?from=DD-MM-YYYY&to=DD-MM-YYYY&all=true
    useEffect(() => {
        const params = new URLSearchParams(props.location && props.location.search)
        const fromParam = params.get('from')
        const toParam = params.get('to')
        const allParam = params.get('all')

        const startDate = fromParam ? moment(fromParam, 'DD-MM-YYYY', true) : null
        const endDate = toParam ? moment(toParam, 'DD-MM-YYYY', true) : null

        if (startDate && startDate.isValid() && endDate && endDate.isValid()) {
            setFormData(prev => ({
                ...prev,
                startDate,
                endDate,
                startTime: '00:00:00',
                endTime: '23:59:59'
            }))
            props.form.setFieldsValue({ startDate, endDate })
        }

        if (allParam === 'true') {
            setAllAssetChecked(true)
            props.form.setFieldsValue({ assets: [] })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Convert HH:MM:SS to seconds
    const convertTimeToSeconds = (timeString) => {
        const [hours, minutes, seconds] = timeString.split(':').map(Number)
        return hours * 3600 + minutes * 60 + seconds
    }

    // Convert seconds to HH:MM:SS format
    const formatTimeFromSeconds = (seconds) => {
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const secs = seconds % 60
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    // Check if the selected time range represents a full day
    const isFullDayRange = (startMoment, endMoment) => {
        const startOfDay = startMoment.clone().startOf('day')
        const endOfDay = startMoment.clone().endOf('day')

        // Check if start time is within 5 minutes of 00:00:00
        const startDiffMinutes = Math.abs(startMoment.diff(startOfDay, 'minutes'))

        // Check if end time is within 5 minutes of 23:59:59
        const endDiffMinutes = Math.abs(endMoment.diff(endOfDay, 'minutes'))

        // Consider it a full day if both start and end times are within 5 minutes tolerance
        return startDiffMinutes <= 5 && endDiffMinutes <= 5
    }

    const handleStartDateChange = (date) => {
        if (date) {
            // Set start time to beginning of day
            const startTime = '00:00:00'
            const endTime = '23:59:59'

            // Set end date to the same day as start date
            const endDate = date.clone()

            // Update form data
            setFormData(prev => ({
                ...prev,
                startDate: date,
                startTime: startTime,
                endDate: endDate,
                endTime: endTime
            }))

            // Update form fields and validate
            props.form.setFieldsValue({
                startDate: date,
                endDate: endDate
            })

            // Trigger validation for both fields
            props.form.validateFields(['startDate', 'endDate'], { force: true })
        }
    }

    const handleEndDateChange = (date) => {
        if (date) {
            const isToday = date.isSame(moment(), 'day')
            setFormData(prev => ({
                ...prev,
                endDate: date,
                endTime: isToday ? moment().format('HH:mm:ss') : '23:59:59'
            }))
        }
    }

    const handleStartTimeChange = (e) => {
        const newStartTime = e.target.value
        setFormData(prev => ({ ...prev, startTime: newStartTime }))

        // Keep end date the same as start date, but update end time
        const endTime = '23:59:59'

        setFormData(prev => ({
            ...prev,
            endTime: endTime
        }))
    }

    const handleEndTimeChange = (value) => {
        const startMoment = formData.startDate.clone()
        const [startHours, startMinutes, startSeconds] = formData.startTime.split(':').map(Number)
        startMoment.set({ hours: startHours, minutes: startMinutes, seconds: startSeconds })

        const endMoment = formData.endDate.clone()
        const [endHours, endMinutes, endSeconds] = value.split(':').map(Number)
        endMoment.set({ hours: endHours, minutes: endMinutes, seconds: endSeconds })

        // Check if end time is more than 24 hours from start time
        if (endMoment.diff(startMoment, 'days', true) > 30) {
            message.error('End time cannot be more than 30 days from start time')
            return
        }

        // Check if end time is before start time
        if (endMoment.isBefore(startMoment)) {
            message.error('End time cannot be before start time!')
            return
        }

        setFormData(prev => ({ ...prev, endTime: value }))
    }

    const getMaxEndTime = () => {
        if (formData.endDate && formData.endDate.isSame(moment(), 'day')) {
            return convertTimeToSeconds(moment().format('HH:mm:ss'))
        }
        return 86399 // 23:59:59
    }

    const generateDefaultFilename = () => {
        return `iot-daily-report-${Date.now()}`
    }

    const handleDownloadConfirm = () => {
        if (pendingDownload && customFilename.trim()) {
            const { blob, originalFilename } = pendingDownload

            // Use custom filename or fall back to original
            const finalFilename = customFilename.trim().endsWith('.pdf')
                ? customFilename.trim()
                : `${customFilename.trim()}.pdf`

            // Create blob and download
            const url = window.URL.createObjectURL(blob)

            // Create temporary link and trigger download
            const link = document.createElement('a')
            link.href = url
            link.download = finalFilename
            document.body.appendChild(link)
            link.click()

            // Cleanup
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            // Close modal and reset states
            setSaveModalVisible(false)
            setPendingDownload(null)
            setCustomFilename('')

            message.success('Report downloaded successfully!')
        }
    }

    const handleDownloadCancel = () => {
        setSaveModalVisible(false)
        setPendingDownload(null)
        setCustomFilename('')
        setIsSubmitting(false)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        props.form.validateFields((err, values) => {
            if (!err) {
                setIsSubmitting(true)

                // Create moment objects with selected dates and times
                const startMoment = formData.startDate.clone()
                const [startHours, startMinutes, startSeconds] = formData.startTime.split(':').map(Number)
                startMoment.set({ hours: startHours, minutes: startMinutes, seconds: startSeconds })

                const endMoment = formData.endDate.clone()
                const [endHours, endMinutes, endSeconds] = formData.endTime.split(':').map(Number)
                endMoment.set({ hours: endHours, minutes: endMinutes, seconds: endSeconds })

                const now = moment()

                // Validate time range
                if (endMoment.isAfter(now)) {
                    message.error('End time cannot be in the future!')
                    setIsSubmitting(false)
                    return
                }

                if (startMoment.isAfter(endMoment)) {
                    message.error('Start time cannot be after end time!')
                    setIsSubmitting(false)
                    return
                }

                // Check if duration exceeds 24 hours
                const daysDiff = endMoment.diff(startMoment, 'days', true)
                if (daysDiff > 30) {
                    message.error('The selected period cannot exceed 30 days!')
                    setIsSubmitting(false)
                    return
                }

                // Validate that end time is not before start time
                if (endMoment.isBefore(startMoment)) {
                    message.error('End time cannot be before start time!')
                    setIsSubmitting(false)
                    return
                }

                // Check if it's a full day selection
                const isFullDay = isFullDayRange(startMoment, endMoment)

                // Process the form data
                const reportData = {
                    periodFromTimestamp: isFullDay ? startMoment.startOf('day').valueOf() : startMoment.valueOf(),
                    periodToTimestamp: isFullDay ? endMoment.endOf('day').valueOf() : endMoment.valueOf(),
                    assets: allAssetChecked ? ['all'] : (values.assets || []),
                    isFullDay: isFullDay
                }

                axios.post('/iot-device-data/daily-iot-report', reportData, { responseType: 'blob' })
                    .then(response => {
                        setIsSubmitting(false)
                        const contentType = response.headers['content-type']
                        if (response.data && contentType && contentType.includes('application/pdf')) {
                            const contentDisposition = response.headers['content-disposition']
                            let originalFilename = 'iot-device-daily-report.pdf'

                            if (contentDisposition) {
                                const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
                                if (filenameMatch) {
                                    originalFilename = filenameMatch[1]
                                }
                            }

                            // Create blob
                            const blob = new Blob([response.data], { type: 'application/pdf' })

                            // Set up for save dialog
                            setPendingDownload({ blob, originalFilename })
                            setCustomFilename(generateDefaultFilename())
                            setSaveModalVisible(true)

                            // Note: Don't set isSubmitting to false here, it will be set in modal handlers
                        } else {
                            message.error('No report available for the selected criteria.')
                        }
                        // console.log(response)
                    })
                    .catch(error => {
                        setIsSubmitting(false)
                        message.error('Failed to generate report. Please try again.')
                        console.log(error)
                    })
            }
        })
    }

    const handleReset = () => {
        props.form.resetFields()
        setFormData({
            startDate: null,
            endDate: null,
            startTime: '00:00:00',
            endTime: '23:59:59',
            assets: []
        })
        setAllAssetChecked(false)
    }

    const handleAllAssetChange = (e) => {
        const checked = e.target.checked
        setAllAssetChecked(checked)

        if (checked) {
            // Clear assets selection and disable AssetSearch
            props.form.setFieldsValue({ assets: [] })
            setFormData(prev => ({ ...prev, assets: [] }))
            // Clear validation errors for assets field
            props.form.setFields({
                assets: {
                    value: [],
                    errors: null
                }
            })
        } else {
            // When unchecking, validate that assets are selected
            props.form.validateFields(['assets'], { force: true })
        }
    }

    const validateDateTimeRange = (rule, value, callback) => {
        // If we're validating start date and end date is already set, don't show the error
        if (rule.field === 'startDate' && formData.endDate) {
            callback()
            return
        }

        // If we're validating end date and start date is already set, don't show the error
        if (rule.field === 'endDate' && formData.startDate) {
            callback()
            return
        }

        if (!formData.startDate || !formData.endDate) {
            callback('Please select both start and end dates')
            return
        }

        // Create moment objects with selected dates and times
        const startMoment = formData.startDate.clone()
        const [startHours, startMinutes, startSeconds] = formData.startTime.split(':').map(Number)
        startMoment.set({ hours: startHours, minutes: startMinutes, seconds: startSeconds })

        const endMoment = formData.endDate.clone()
        const [endHours, endMinutes, endSeconds] = formData.endTime.split(':').map(Number)
        endMoment.set({ hours: endHours, minutes: endMinutes, seconds: endSeconds })

        const now = moment()

        // Check if end time is in the future
        if (endMoment.isAfter(now)) {
            callback('End time cannot be in the future!')
            return
        }

        // Check if start time is after end time
        if (startMoment.isAfter(endMoment)) {
            callback('Start time cannot be after end time!')
            return
        }

        // Check if duration exceeds 24 hours
        // const hoursDiff = endMoment.diff(startMoment, 'hours', true)
        // if (hoursDiff > 24) {
        //     callback('The selected period cannot exceed 24 hours!')
        //     return
        // }

        // Check if end time is before start time
        if (endMoment.isBefore(startMoment)) {
            callback('End time cannot be before start time!')
            return
        }

        callback()
    }

    const disabledDate = (current) => {
        // Disable dates in the future
        return current && current > moment().endOf('day')
    }

    return (
        <Spin spinning={props.loading || isSubmitting}>
            <Card className="gx-card-metrics" title="IoT Device Daily Report">
                <Form
                    onSubmit={handleSubmit}
                    className="ant-advanced-search-form"
                    layout="horizontal"
                    labelAlign="left"
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                >
                    <Row gutter={24}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                            <Form.Item label="Start Date">
                                {getFieldDecorator('startDate', {
                                    rules: [
                                        { required: true, message: 'Please select start date!' }
                                    ]
                                })(
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="YYYY-MM-DD"
                                        placeholder="Select Start Date"
                                        disabledDate={disabledDate}
                                        value={formData.startDate}
                                        onChange={handleStartDateChange}
                                    />
                                )}
                            </Form.Item>

                            <Form.Item label="Start Time">
                                <Input
                                    type="time"
                                    value={formData.startTime}
                                    onChange={handleStartTimeChange}
                                    style={{ width: '100%' }}
                                    step="1"
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                            <Form.Item label="End Date">
                                {getFieldDecorator('endDate', {
                                    rules: [
                                        { required: true, message: 'Please select end date!' }
                                    ]
                                })(
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="YYYY-MM-DD"
                                        placeholder="Select End Date"
                                        disabledDate={disabledDate}
                                        value={formData.endDate}
                                        onChange={handleEndDateChange}
                                    />
                                )}
                            </Form.Item>

                            <Form.Item label="End Time">
                                <Input
                                    type="time"
                                    value={formData.endTime}
                                    onChange={(e) => handleEndTimeChange(e.target.value)}
                                    style={{ width: '100%' }}
                                    step="1"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                            <Form.Item label="Assets">
                                <Row gutter={16}>
                                    <Col xs={24} sm={6} md={4} lg={4} xl={4}>
                                        <Checkbox
                                            checked={allAssetChecked}
                                            onChange={handleAllAssetChange}
                                        >
                                            All asset
                                        </Checkbox>
                                    </Col>
                                    <Col xs={24} sm={18} md={20} lg={20} xl={20}>
                                        {getFieldDecorator('assets', {
                                            rules: [
                                                {
                                                    required: !allAssetChecked,
                                                    message: 'Please select at least one asset!'
                                                }
                                            ]
                                        })(
                                            <AssetSearch
                                                styleName="gx-lt-icon-search-bar-lg"
                                                placeholder="Search and select assets..."
                                                onChange={(keyword) => props.searchGlobalAssetData(keyword, true)}
                                                assetData={props.assetData && props.assetData.filter((ast) => ast.assetModel)}
                                                onSelect={(values) => {
                                                    props.form.setFieldsValue({ assets: values })
                                                    setFormData(prev => ({ ...prev, assets: values }))
                                                }}
                                                authUser={props.authUser}
                                                loading={props.assetSearchLoading}
                                                multiple={true}
                                                disabled={allAssetChecked}
                                            />
                                        )}
                                    </Col>
                                </Row>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                            <Form.Item wrapperCol={{ span: 24 }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                    <Button
                                        type="default"
                                        onClick={handleReset}
                                        disabled={isSubmitting}
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={isSubmitting}
                                        icon="download"
                                    >
                                        Download Report
                                    </Button>
                                </div>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* Save File Modal */}
            <Modal
                title="Save Report"
                visible={saveModalVisible}
                onOk={handleDownloadConfirm}
                onCancel={handleDownloadCancel}
                okText="Download"
                cancelText="Cancel"
                okButtonProps={{ disabled: !customFilename.trim() }}
                width={500}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>Please enter a filename for your report:</Text>
                </div>
                <Input
                    value={customFilename}
                    onChange={(e) => setCustomFilename(e.target.value)}
                    placeholder="Enter filename..."
                    suffix=".pdf"
                    style={{ width: '100%' }}
                    onPressEnter={handleDownloadConfirm}
                    autoFocus
                />
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        The file will be saved with .pdf extension automatically
                    </Text>
                </div>
            </Modal>
        </Spin>
    )
}

export default connect(mapStateToProps, mapDispatchToProps)(Form.create()(DailyReport))
